// src/components/ui/StepHtmlActivityBubble.tsx

import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { CheckCircle2, Maximize2, Minimize2, X } from "lucide-react";
import { useDictation } from "../../hooks/useDictation";

/**
 * Generic HTML activity artifact
 *
 * Supports current ERP tools and future embedded activities:
 * - erp_reader
 * - erp_writer
 * - anxiety_body_map
 * - breather
 * - doodler
 *
 * Current n8n contract:
 * {
 *   id: "step-1-erp_reader",
 *   stepId: "1",
 *   type: "html_activity",
 *   activityKey: "erp_reader",
 *   label: "ERP Reader",
 *   url: "/activities/erp-reader.html",
 *   hint: "Read the script slowly..."
 * }
 *
 * Also supports newer/future shapes:
 * {
 *   config: {
 *     url: "/activities/example.html",
 *     htmlFileName: "example.html"
 *   },
 *   htmlFileName: "example.html"
 * }
 */
export type HtmlActivityArtifact = {
  id: string;
  stepId: string;
  label: string;
  title?: string;
  hint?: string;
  type: "html_activity" | "htmlActivity";
  renderType?: "htmlActivity" | string;
  activityKey: string;
  url?: string;
  htmlFileName?: string;
  config?: {
    url?: string;
    htmlFileName?: string;

    /**
     * Optional duration used by HTML activities with their own timer.
     * Existing activities may omit both fields.
     */
    sessionDurationSeconds?: number;
    durationLocked?: boolean;
  };
};

type HtmlActivityEvent = {
  widgetType: "coping_buddy" | "learning_buddy";
  widgetLabel: "Coping Buddy" | "Learning Buddy";
  activityKey: string;
  activityTitle: string;
  eventType: "started" | "completed" | "used" | "saved" | "submitted";
  metadata?: Record<string, unknown>;
};

type ActiveDictationRequest = {
  requestId: number | null;
  activityKey: string;
  activityTitle: string;
  targetField: string;
  targetWindow: Window;
  targetOrigin: string;
};

type Props = {
  artifact: HtmlActivityArtifact;
  sessionId: string;
  accessCode?: string;

  /** Parent triggers advancing the step */
  onFinished: () => void;

  /** Optional activity-event capture for embedded HTML widgets */
  onActivityEvent?: (event: HtmlActivityEvent) => void;

  /** Lets ChatContainer lock its inner chat scroller while expanded */
  onExpandedChange?: (expanded: boolean) => void;
};

export type StepHtmlActivityBubbleHandle = {
  executeVoiceCommand: (raw: string) => boolean;
};

type HtmlActivityUiConfig = {
  icon: string;
  fallbackPath: string;
  source: string;
  eventPrefix: string;
  loadingText: string;
  readyText: string;
  openTitle: string;
};

const HTML_ACTIVITY_UI: Record<string, HtmlActivityUiConfig> = {
  erp_reader: {
    icon: "📖",
    fallbackPath: "/activities/erp-reader.html",
    source: "erp_reader",
    eventPrefix: "ERP_READER",
    loadingText: "Loading ERP Reader…",
    readyText: "ERP Reader ready",
    openTitle: "Maximize ERP Reader",
  },

  erp_writer: {
    icon: "✍️",
    fallbackPath: "/activities/erp_writer.html",
    source: "erp_writer",
    eventPrefix: "ERP_WRITER",
    loadingText: "Loading ERP Writer…",
    readyText: "ERP Writer ready",
    openTitle: "Maximize ERP Writer",
  },

  anxiety_body_map: {
    icon: "🧠",
    fallbackPath: "/activities/anxiety_body_map_updated_v7.html",
    source: "anxiety_body_map",
    eventPrefix: "ANXIETY_BODY_MAP",
    loadingText: "Loading Anxiety Body Map…",
    readyText: "Anxiety Body Map ready",
    openTitle: "Maximize Anxiety Body Map",
  },

  breather: {
    icon: "🫁",
    fallbackPath: "/activities/breather.html",
    source: "breather",
    eventPrefix: "BREATHER",
    loadingText: "Loading Breather…",
    readyText: "Breather ready",
    openTitle: "Maximize Breather",
  },

  doodler: {
    icon: "✏️",
    fallbackPath: "/activities/doodler.html",
    source: "doodler",
    eventPrefix: "DOODLER",
    loadingText: "Loading Doodler…",
    readyText: "Doodler ready",
    openTitle: "Maximize Doodler",
  },
};

const DEFAULT_UI: HtmlActivityUiConfig = {
  icon: "🧩",
  fallbackPath: "/activities/index.html",
  source: "html_activity",
  eventPrefix: "HTML_ACTIVITY",
  loadingText: "Loading activity…",
  readyText: "Activity ready",
  openTitle: "Maximize activity",
};

function normalizeHtmlActivityUrl(rawInput: string, fallbackPath: string): string {
  const raw = (rawInput || "").trim();

  if (!raw) return fallbackPath;

  if (
    raw === "/" ||
    raw === "./" ||
    raw === window.location.origin ||
    raw === window.location.origin + "/"
  ) {
    return fallbackPath;
  }

  try {
    const u = new URL(raw, window.location.origin);
    const isAbsolute = /^https?:\/\//i.test(raw);
    return isAbsolute ? u.toString() : `${u.pathname}${u.search}${u.hash}`;
  } catch {
    return raw;
  }
}

const StepHtmlActivityBubble = React.forwardRef<
  StepHtmlActivityBubbleHandle,
  Props
>(({
  artifact,
  sessionId,
  accessCode,
  onFinished,
  onActivityEvent,
  onExpandedChange,
}, ref) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const finishedOnceRef = useRef(false);

  const [loaded, setLoaded] = useState(false);
  const [finished, setFinished] = useState(false);
  const [iframeHeight, setIframeHeight] = useState(700);
  const [isExpanded, setIsExpanded] = useState(false);

  // Reuse efa-chat's existing browser dictation hook for iframe activities.
  const {
    transcript: dictationTranscript,
    isListening: isDictationListening,
    toggle: toggleDictation,
  } = useDictation();

  const activeDictationRequestRef = useRef<ActiveDictationRequest | null>(null);
  const activeDictationSessionIdRef = useRef<number | null>(null);
  const latestDictationTextRef = useRef("");
  const prevIsDictationListeningRef = useRef(false);

  const activityKey = useMemo(() => {
    return String(artifact?.activityKey || "").trim().toLowerCase();
  }, [artifact?.activityKey]);

  const uiCfg = useMemo(() => {
    return HTML_ACTIVITY_UI[activityKey] || DEFAULT_UI;
  }, [activityKey]);

  const url = useMemo(() => {
    const raw =
      artifact?.config?.url ||
      artifact?.url ||
      (artifact?.htmlFileName ? `/activities/${artifact.htmlFileName}` : "") ||
      (artifact?.config?.htmlFileName
        ? `/activities/${artifact.config.htmlFileName}`
        : "");

    return normalizeHtmlActivityUrl(String(raw || "").trim(), uiCfg.fallbackPath);
  }, [
    artifact?.config?.url,
    artifact?.config?.htmlFileName,
    artifact?.url,
    artifact?.htmlFileName,
    uiCfg.fallbackPath,
  ]);

  useEffect(() => {
    console.log(
      "[StepHtmlActivityBubble]",
      "activityKey =",
      activityKey,
      "artifact.url =",
      artifact?.url,
      "artifact.config?.url =",
      artifact?.config?.url,
      "artifact.htmlFileName =",
      artifact?.htmlFileName,
      "=> iframe url =",
      url
    );
  }, [
    activityKey,
    artifact?.url,
    artifact?.config?.url,
    artifact?.htmlFileName,
    url,
  ]);

  const iframeOrigin = useMemo(() => {
    try {
      return new URL(url, window.location.href).origin;
    } catch {
      return window.location.origin;
    }
  }, [url]);

  const isSameOriginActivity = iframeOrigin === window.location.origin;


  // --------------------------------------------------
  // Voice controls for supported embedded HTML activities.
  // --------------------------------------------------
  useImperativeHandle(
    ref,
    () => ({
      executeVoiceCommand: (raw: string): boolean => {
        if (
          activityKey !== "butterfly_breath" ||
          !isSameOriginActivity ||
          !loaded
        ) {
          return false;
        }

        const spoken = String(raw || "")
          .toLowerCase()
          .replace(/\[[^\]]*\]/g, " ")
          .replace(/[^a-z\s]/g, " ")
          .replace(/\s+/g, " ")
          .trim();

        const command =
          spoken === "start" || spoken === "start breathing"
            ? "start"
            : spoken === "pause" || spoken === "pause breathing"
              ? "pause"
              : spoken === "resume" || spoken === "resume breathing"
                ? "resume"
                : spoken === "reset" || spoken === "reset breathing"
                  ? "reset"
                  : null;

        if (!command) return false;

        const iframeWindow = iframeRef.current?.contentWindow;
        if (!iframeWindow) return false;

        try {
          iframeWindow.postMessage(
            {
              source: "efa-chat",
              type: "ACTIVITY_VOICE_CONTROL",
              activityKey: "butterfly_breath",
              command,
            },
            iframeOrigin
          );

          console.info(
            "[Butterfly Breath] voice command forwarded",
            { command }
          );

          return true;
        } catch (error) {
          console.warn(
            "[Butterfly Breath] voice command failed",
            error
          );
          return false;
        }
      },
    }),
    [activityKey, isSameOriginActivity, loaded, iframeOrigin]
  );

  const finishType = useMemo(
    () => `${uiCfg.eventPrefix}_FINISHED`,
    [uiCfg.eventPrefix]
  );

  const heightType = useMemo(
    () => `${uiCfg.eventPrefix}_HEIGHT`,
    [uiCfg.eventPrefix]
  );

  const postDictationMessage = useCallback(
    (
      request: ActiveDictationRequest,
      payload: {
        type: "DICTATION_STATUS" | "DICTATION_RESULT";
        status?: "listening" | "stopped" | "unsupported" | "error";
        transcript?: string;
      }
    ) => {
      try {
        request.targetWindow.postMessage(
          {
            source: "efa-chat",
            ...payload,
            activityKey: request.activityKey,
            activityTitle: request.activityTitle,
            targetField: request.targetField,
            requestId: request.requestId,
          },
          request.targetOrigin
        );
      } catch (err) {
        console.warn("[StepHtmlActivityBubble] dictation postMessage failed:", err);
      }
    },
    []
  );

  const safeFinish = useCallback(() => {
    if (finishedOnceRef.current) return;
    finishedOnceRef.current = true;

    setFinished(true);
    onFinished();
  }, [onFinished]);

  useEffect(() => {
    finishedOnceRef.current = false;
    setFinished(false);
    setLoaded(false);
    setIsExpanded(false);
  }, [artifact?.id]);

  useEffect(() => {
    onExpandedChange?.(isExpanded);

    return () => {
      onExpandedChange?.(false);
    };
  }, [isExpanded, onExpandedChange]);

  useEffect(() => {
    if (!isExpanded) return;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousBodyOverscroll = document.body.style.overscrollBehavior;
    const previousHtmlOverscroll =
      document.documentElement.style.overscrollBehavior;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    document.body.style.overscrollBehavior = "none";
    document.documentElement.style.overscrollBehavior = "none";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.overscrollBehavior = previousBodyOverscroll;
      document.documentElement.style.overscrollBehavior = previousHtmlOverscroll;
    };
  }, [isExpanded]);

  useEffect(() => {
    const onMessage = (evt: MessageEvent) => {
      const iframeWin = iframeRef.current?.contentWindow;

      if (!iframeWin || evt.source !== iframeWin) return;

      if (evt.origin !== "null" && evt.origin !== iframeOrigin) return;

      const data = evt.data;
      if (!data || typeof data !== "object") return;

      const source = String((data as any).source ?? "");
      const type = String((data as any).type ?? "");

      if (source === "efa-activity" && type === "REQUEST_DICTATION") {
        const targetWindow = evt.source as Window | null;
        if (!targetWindow) return;

        const request: ActiveDictationRequest = {
          requestId:
            typeof (data as any).requestId === "number"
              ? (data as any).requestId
              : null,
          activityKey:
            typeof (data as any).activityKey === "string" &&
              (data as any).activityKey.trim()
              ? (data as any).activityKey.trim()
              : artifact.activityKey || artifact.id || "html_activity",
          activityTitle:
            typeof (data as any).activityTitle === "string" &&
              (data as any).activityTitle.trim()
              ? (data as any).activityTitle.trim()
              : artifact.title || artifact.label || "Activity",
          targetField:
            typeof (data as any).targetField === "string" &&
              (data as any).targetField.trim()
              ? (data as any).targetField.trim()
              : "text",
          targetWindow,
          targetOrigin: evt.origin === "null" ? "*" : evt.origin,
        };

        // If the same activity taps the mic while listening, treat it as Stop.
        if (activeDictationRequestRef.current && isDictationListening) {
          toggleDictation();
          postDictationMessage(request, {
            type: "DICTATION_STATUS",
            status: "stopped",
          });
          return;
        }

        activeDictationRequestRef.current = request;
        latestDictationTextRef.current = "";

        const dictationSessionId = toggleDictation();

        if (typeof dictationSessionId !== "number") {
          activeDictationRequestRef.current = null;
          activeDictationSessionIdRef.current = null;

          postDictationMessage(request, {
            type: "DICTATION_STATUS",
            status: "unsupported",
          });
          return;
        }

        activeDictationSessionIdRef.current = dictationSessionId;

        postDictationMessage(request, {
          type: "DICTATION_STATUS",
          status: "listening",
        });

        return;
      }

      if (source === "efa-activity" && type === "ACTIVITY_EVENT") {
        const rawEventType = String((data as any).eventType ?? "")
          .toLowerCase()
          .trim();

        const eventTypeMap: Record<string, HtmlActivityEvent["eventType"]> = {
          open: "started",
          opened: "started",
          start: "started",
          started: "started",
          complete: "completed",
          completed: "completed",
          first_action: "used",
          used: "used",
          save: "saved",
          saved: "saved",
          submit: "submitted",
          submitted: "submitted",
        };

        const eventType = eventTypeMap[rawEventType];
        if (!eventType) return;

        onActivityEvent?.({
          widgetType:
            (data as any).widgetType === "learning_buddy"
              ? "learning_buddy"
              : "coping_buddy",
          widgetLabel:
            (data as any).widgetType === "learning_buddy"
              ? "Learning Buddy"
              : "Coping Buddy",
          activityKey:
            typeof (data as any).activityKey === "string" &&
              (data as any).activityKey.trim()
              ? (data as any).activityKey.trim()
              : artifact.activityKey || artifact.id || "html_activity",
          activityTitle:
            typeof (data as any).activityTitle === "string" &&
              (data as any).activityTitle.trim()
              ? (data as any).activityTitle.trim()
              : artifact.title || artifact.label || "Activity",
          eventType,
          metadata: {
            renderType: "htmlActivity",
            ...((data as any).metadata &&
              typeof (data as any).metadata === "object"
              ? (data as any).metadata
              : {}),
          },
        });

        return;
      }

      if (source !== uiCfg.source) return;

      if (type === heightType) {
        const h = Number((data as any).payload?.height);
        if (Number.isFinite(h) && h > 0) {
          const next = Math.min(Math.max(h + 80, 520), 900);

          setIframeHeight((prev) => (Math.abs(prev - next) >= 12 ? next : prev));

          console.log("[StepHtmlActivityBubble] HEIGHT", {
            activityKey,
            origin: evt.origin,
            iframeOrigin,
            h,
          });
        }
        return;
      }

      if (type === finishType) {
        // A writing sprint is repeatable; only the explicit finish action
        // completes homework. Ignore legacy automatic FINISHED events too.
        if (
          activityKey === "erp_writer" &&
          (data as any).payload?.reason !== "practice_finished"
        ) {
          return;
        }
        safeFinish();
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [
    activityKey,
    artifact.activityKey,
    artifact.id,
    artifact.label,
    artifact.title,
    finishType,
    heightType,
    iframeOrigin,
    isDictationListening,
    onActivityEvent,
    postDictationMessage,
    safeFinish,
    toggleDictation,
    uiCfg.source,
  ]);

  useEffect(() => {
    const activeRequest = activeDictationRequestRef.current;
    if (!activeRequest || !dictationTranscript) return;

    if (dictationTranscript.sessionId !== activeDictationSessionIdRef.current) {
      return;
    }

    latestDictationTextRef.current = dictationTranscript.text || "";
  }, [dictationTranscript]);

  useEffect(() => {
    const wasListening = prevIsDictationListeningRef.current;
    prevIsDictationListeningRef.current = isDictationListening;

    if (!wasListening || isDictationListening) return;

    const activeRequest = activeDictationRequestRef.current;
    if (!activeRequest) return;

    const finalText = latestDictationTextRef.current.trim();

    if (finalText) {
      postDictationMessage(activeRequest, {
        type: "DICTATION_RESULT",
        transcript: finalText,
      });
    } else {
      postDictationMessage(activeRequest, {
        type: "DICTATION_STATUS",
        status: "stopped",
      });
    }

    activeDictationRequestRef.current = null;
    activeDictationSessionIdRef.current = null;
    latestDictationTextRef.current = "";
  }, [isDictationListening, postDictationMessage]);

  useEffect(() => {
    return () => {
      activeDictationRequestRef.current = null;
      activeDictationSessionIdRef.current = null;
      latestDictationTextRef.current = "";
    };
  }, []);

  const postContextToIframe = useCallback(() => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;

    // Context passing is supported only for same-origin activities.
    if (iframeOrigin !== window.location.origin) return;

    const configuredDuration = Number(
      artifact.config?.sessionDurationSeconds
    );

    const hasConfiguredDuration =
      Number.isFinite(configuredDuration) &&
      configuredDuration > 0;

    try {
      win.postMessage(
        {
          type: "efa.activity.context",
          sessionId,
          accessCode: accessCode || null,
          stepId: artifact.stepId,
          artifactId: artifact.id,
          activityKey,

          // Backward compatible: omit when not supplied.
          ...(hasConfiguredDuration
            ? {
              sessionDurationSeconds:
                configuredDuration,
            }
            : {}),

          // Backward compatible: omit when not supplied.
          ...(typeof artifact.config?.durationLocked ===
            "boolean"
            ? {
              durationLocked:
                artifact.config.durationLocked,
            }
            : {}),
        },
        iframeOrigin
      );
    } catch {
      // Ignore context-posting failures so the activity
      // continues working with its existing defaults.
    }
  }, [
    accessCode,
    activityKey,
    artifact.config?.durationLocked,
    artifact.config?.sessionDurationSeconds,
    artifact.id,
    artifact.stepId,
    iframeOrigin,
    sessionId,
  ]);

  return (
    <div
      className={[
        "w-full rounded-2xl bg-white shadow p-4 border border-zinc-200",
        isExpanded ? "overflow-visible" : "overflow-hidden",
      ].join(" ")}
    >
      <div className="flex items-start gap-3">
        <div
          className="shrink-0 w-9 h-9 rounded-full bg-zinc-100 flex items-center justify-center"
          aria-hidden
        >
          <span className="text-lg leading-none">{uiCfg.icon}</span>
        </div>

        <div className="w-full">
          <div className="flex items-center gap-2">
            <h3 className="text-zinc-900 font-semibold">
              {artifact.title || artifact.label}
            </h3>

            {finished && (
              <span className="inline-flex items-center gap-1 text-emerald-700 text-sm">
                <CheckCircle2 className="w-4 h-4" /> Done
              </span>
            )}
          </div>

          {artifact.hint && (
            <p className="text-zinc-500 text-sm mt-0.5">{artifact.hint}</p>
          )}

          {isExpanded && (
            <div
              className="fixed inset-0 z-[99998] bg-slate-950/70"
              aria-hidden="true"
            />
          )}

          <div
            className={
              isExpanded
                ? "fixed inset-3 z-[99999] flex flex-col overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl sm:inset-5"
                : "mt-3 overflow-hidden rounded-xl border border-zinc-200"
            }
          >
            <div
              className={[
                "flex items-start justify-between gap-3 border-b border-zinc-200 px-3 py-2",
                isExpanded ? "bg-white sm:px-4 sm:py-3" : "bg-zinc-50",
              ].join(" ")}
            >
              <div>
                {isExpanded ? (
                  <>
                    <div className="text-sm font-semibold text-zinc-900">
                      {artifact.title || artifact.label}
                    </div>

                    {artifact.hint ? (
                      <div className="mt-1 text-xs text-zinc-500">
                        {artifact.hint}
                      </div>
                    ) : null}

                    {!loaded ? (
                      <div className="mt-1 text-xs text-zinc-500">
                        {uiCfg.loadingText}
                      </div>
                    ) : null}
                  </>
                ) : !loaded ? (
                  <div className="text-xs text-zinc-600">
                    {uiCfg.loadingText}
                  </div>
                ) : null}
              </div>

              {isExpanded ? (
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsExpanded(false)}
                    className="hidden items-center gap-2 rounded-full border border-zinc-300 bg-white px-3 py-1.5 text-xs font-semibold text-zinc-700 shadow-sm hover:bg-zinc-50 focus:outline-none focus:ring-4 focus:ring-zinc-100 sm:inline-flex"
                    aria-label={`Exit maximized ${artifact.title || artifact.label
                      }`}
                  >
                    <Minimize2 className="h-3.5 w-3.5" />
                    Minimize
                  </button>

                  <button
                    type="button"
                    onClick={() => setIsExpanded(false)}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-zinc-300 bg-white text-zinc-700 shadow-sm hover:bg-zinc-50 focus:outline-none focus:ring-4 focus:ring-zinc-100"
                    aria-label="Close expanded activity"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsExpanded(true)}
                  className="inline-flex items-center gap-1 text-xs text-zinc-700 hover:underline"
                  title={uiCfg.openTitle}
                  aria-label={uiCfg.openTitle}
                >
                  Maximize <Maximize2 className="h-3 w-3" />
                </button>
              )}
            </div>

            <iframe
              ref={iframeRef}
              title={artifact.title || artifact.label}
              src={url}
              className={
                isExpanded
                  ? "min-h-0 w-full flex-1 overscroll-contain border-0 bg-white"
                  : "w-full border-0"
              }
              style={isExpanded ? undefined : { height: iframeHeight }}
              {...(isSameOriginActivity
                ? {}
                : {
                  sandbox:
                    "allow-scripts allow-forms allow-same-origin allow-popups allow-downloads allow-modals",
                })}
              allow="clipboard-write; microphone"
              loading="lazy"
              onLoad={() => {
                setLoaded(true);
                postContextToIframe();
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

StepHtmlActivityBubble.displayName = "StepHtmlActivityBubble";

export default StepHtmlActivityBubble;
